function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { FC, CSSProperties, ReactElement, RefObject, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { mergeAllRefs } from '../../lib/mergeRefs';
require("./Table.css");
var styles = {
  "Wrapper": "Table-module-Wrapper_3dr",
  "Root": "Table-module-Root_1Jw",
  "resizable": "Table-module-resizable_2Ef",
  "stickyHeader": "Table-module-stickyHeader_iFs",
  "defaultVariant": "Table-module-defaultVariant_AMf",
  "highlightedEvenVariant": "Table-module-highlightedEvenVariant_2Cb",
  "highlightedOddVariant": "Table-module-highlightedOddVariant_2nS"
};
export const TableVariants = {
  default: 'default',
  highlightedOdd: 'highlightedOdd',
  highlightedEven: 'highlightedEven'
};
export const TableContext = /*#__PURE__*/React.createContext({
  resizable: false,
  stickyLeftCols: [],
  stickyRightCols: []
});
export const Table = ({
  children,
  variant = TableVariants.default,
  resizable = false,
  stickyHeader = false,
  stickyLeftCols = [],
  stickyRightCols = [],
  initSizes = [],
  onSetSizes,
  innerRef,
  controlRef,
  className,
  style,
  ...props
}) => {
  const [isTableWidthInit, setIsTableWidthInit] = useState(false);
  const colGroupRef = useRef(null);
  const moveData = useRef({});
  const [rootNode, setRootNode] = useState();
  const [resizeOffset, setResizeOffset] = useState(0);
  const onMouseDown = useCallback(e => {
    const target = e.target;
    if (target.dataset.role === 'resizer' && rootNode) {
      moveData.current.activeCell = target.parentNode;
      moveData.current.activeCellInitialWidth = target.parentNode.clientWidth;
      moveData.current.tableWidth = rootNode.clientWidth;
      const coords = target.getBoundingClientRect();
      const offset = e.clientX - coords.left - coords.width / 2;
      setResizeOffset(offset < 0 ? Math.floor(offset) : Math.ceil(offset));
    }
  }, [rootNode]);
  const onMouseMove = useCallback(e => {
    if (!moveData.current?.activeCell || !colGroupRef.current || !rootNode) return;
    const {
      activeCell,
      activeCellInitialWidth,
      tableWidth
    } = moveData.current;
    if (!activeCell || !activeCellInitialWidth || !tableWidth) return;
    const width = Math.max(e.clientX - activeCell.getBoundingClientRect().left - resizeOffset, 50);
    // @ts-ignore
    colGroupRef.current.children[activeCell.cellIndex].style.width = `${width}px`;
    rootNode.style.width = `${tableWidth + (width - activeCellInitialWidth)}px`;
  }, [resizeOffset, rootNode]);
  const onMouseUp = useCallback(() => {
    delete moveData.current.activeCell;
    delete moveData.current.activeCellInitialWidth;
    delete moveData.current.tableWidth;
    const cols = rootNode?.getElementsByTagName('col');
    if (onSetSizes && cols) {
      const colsWidth = [];
      for (let col of cols) {
        colsWidth.push(parseFloat(col.style.width));
      }
      onSetSizes(colsWidth);
    }
  }, [rootNode, onSetSizes]);
  useEffect(() => {
    if (!resizable || typeof window === 'undefined') return;
    document.addEventListener('pointerdown', onMouseDown);
    document.addEventListener('pointermove', onMouseMove);
    document.addEventListener('pointerup', onMouseUp);
    return () => {
      document.removeEventListener('pointerdown', onMouseDown);
      document.removeEventListener('pointermove', onMouseMove);
      document.removeEventListener('pointerup', onMouseUp);
    };
  }, [resizable, onMouseDown, onMouseMove, onMouseUp]);
  const onRefChange = useCallback(node => {
    if (node && resizable) {
      setRootNode(node);
      if (!isTableWidthInit) {
        // берем ширину под контроль, необходимо для корректного ресайза
        node.style.width = initSizes.length ? `${initSizes.reduce((accum, size) => accum + size, 0)}px` : `${node.clientWidth}px`;
        setIsTableWidthInit(true);
      }
    }
    return node;
  },
  // не реагируем на initSizes
  // eslint-disable-next-line
  [resizable, isTableWidthInit]);
  const cols = useMemo(() => {
    if (!rootNode) return null;
    return [].slice.call(rootNode.rows[0].cells).map((cell, index) => /*#__PURE__*/React.createElement("col", {
      key: index,
      style: {
        width: initSizes[index] || cell.clientWidth
      }
    }));
    // не реагируем на initSizes
    // eslint-disable-next-line
  }, [rootNode]);
  return /*#__PURE__*/React.createElement(TableContext.Provider, {
    value: {
      resizable,
      stickyLeftCols,
      stickyRightCols
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.Wrapper
  }, /*#__PURE__*/React.createElement("table", _extends({}, props, {
    ref: mergeAllRefs(onRefChange, innerRef, controlRef),
    className: cn(styles.Root, styles[`${variant}Variant`], {
      [styles.stickyHeader]: stickyHeader,
      [styles.resizable]: resizable
    }, className)
  }), resizable && rootNode && rootNode.rows && /*#__PURE__*/React.createElement("colgroup", {
    ref: colGroupRef
  }, cols), children)));
};
if (process.env.NODE_ENV !== 'production') {
  Table.displayName = 'Table';
}